import { useState } from 'react'
import { api } from '../api/client'
import { useLoadable, ErrorBlock, EmptyBlock, Pill } from '../components/ui'
import type { Inbox, InboxItem, RequestOutcomeKind } from '../types'

/* T4b — the owner's inbox. Requests routed to you, open first.
   Recording an outcome is not a grant: the portal changes nothing in Google.
   You add the person to the folder's access group yourself, then say so here. */

const OUTCOME_LABEL: Record<RequestOutcomeKind, string> = {
  'added-in-google': 'Added in Google',
  declined: 'Declined',
  redirected: 'Redirected',
}

const when = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })

function OpenRequest({ item, onDone }: { item: InboxItem; onDone: (i: InboxItem) => void }) {
  const [mode, setMode] = useState<'idle' | 'redirect' | 'decline'>('idle')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const resolve = async (outcome: RequestOutcomeKind) => {
    setBusy(true); setError(null)
    try {
      const { item: updated } = await api.resolveRequest(item.requestId, outcome, note)
      onDone(updated)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not record that.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="block" style={{ borderLeft: '3px solid var(--accent)', marginBottom: 'var(--s-md)' }}>
      <p className="eyebrow" style={{ marginBottom: 'var(--s-xs)' }}>{item.driveName}</p>
      <h3 style={{ marginTop: 0 }}>{item.folderName}</h3>
      <p style={{ marginBottom: 'var(--s-xs)' }}>
        <b>{item.requesterEmail}</b> asked on {when(item.createdAt)}
      </p>
      <p className="note" style={{ marginTop: 0 }}>Reason: {item.reason}</p>
      {item.routedAsInterim && (
        <p className="note">
          <Pill tone="amber">Interim</Pill> This folder has no owner on file, so it came to you as the
          interim RMS contact. Redirect it to the person who should decide.
        </p>
      )}

      {mode === 'idle' && (
        <div style={{ display: 'flex', gap: 'var(--s-sm)', flexWrap: 'wrap', alignItems: 'center' }}>
          {!item.routedAsInterim && (
            <button className="btn btn--primary" disabled={busy} onClick={() => resolve('added-in-google')}>
              I added them in Google
            </button>
          )}
          {!item.routedAsInterim && (
            <button className="btn btn--secondary" disabled={busy} onClick={() => setMode('decline')}>Decline</button>
          )}
          <button className="btn btn--ghost" disabled={busy} onClick={() => setMode('redirect')}>Redirect →</button>
        </div>
      )}

      {mode !== 'idle' && (
        <div style={{ display: 'grid', gap: 'var(--s-sm)', maxWidth: 520 }}>
          <label htmlFor={`note-${item.requestId}`}>
            {mode === 'redirect' ? 'Who should decide this? (required)' : 'Reason for declining (optional, the requester does not see it yet)'}
          </label>
          <input className="field" id={`note-${item.requestId}`} value={note} onChange={(e) => setNote(e.target.value)}
                 placeholder={mode === 'redirect' ? 'e.g. Smita Sharma, Legal' : 'e.g. Not needed for their role'} />
          <div style={{ display: 'flex', gap: 'var(--s-sm)', flexWrap: 'wrap' }}>
            <button className="btn btn--primary" disabled={busy || (mode === 'redirect' && note.trim().length < 3)}
                    onClick={() => resolve(mode === 'redirect' ? 'redirected' : 'declined')}>
              {mode === 'redirect' ? 'Record redirect' : 'Record decline'}
            </button>
            <button className="btn btn--ghost" disabled={busy} onClick={() => { setMode('idle'); setNote('') }}>Cancel</button>
          </div>
        </div>
      )}

      {error && <p role="alert" style={{ marginBottom: 0 }}><Pill tone="red">Not recorded</Pill> {error}</p>}
    </div>
  )
}

function ClosedRequest({ item }: { item: InboxItem }) {
  return (
    <tr>
      <td style={{ color: 'var(--ink)', fontWeight: 500 }}>{item.folderName}<br /><span className="note">{item.driveName}</span></td>
      <td>{item.requesterEmail}</td>
      <td style={{ whiteSpace: 'nowrap' }}>
        <Pill>{item.outcome ? OUTCOME_LABEL[item.outcome] : item.status}</Pill>
        {item.outcomeNote && <><br /><span className="note">{item.outcomeNote}</span></>}
      </td>
      <td style={{ whiteSpace: 'nowrap' }}>{item.resolvedAt ? when(item.resolvedAt) : '—'}</td>
    </tr>
  )
}

export default function RequestsRoute() {
  const [inbox, retry] = useLoadable<Inbox>(() => api.getInbox(), (d) => d.items.length === 0, [])
  const [updates, setUpdates] = useState<Record<string, InboxItem>>({})

  if (inbox.status === 'error') return <ErrorBlock message={inbox.message} correlationId={inbox.correlationId} onRetry={retry} />

  const items = inbox.status === 'ready' ? inbox.data.items.map((i) => updates[i.requestId] ?? i) : []
  const open = items.filter((i) => i.status === 'open')
  const closed = items.filter((i) => i.status !== 'open')

  return (
    <>
      <p className="eyebrow">Requests</p>
      <h1 style={{ marginBottom: 'var(--s-sm)' }}>
        {inbox.status === 'ready' ? (open.length ? `${open.length} waiting for you` : 'Nothing waiting for you') : 'Access requests for your folders'}
      </h1>
      <p className="note" style={{ marginBottom: 'var(--s-md)' }}>
        People asked for folders you own. To give access, add them to the folder's access group in
        Google Groups (groups.google.com), then tell the portal here. The portal records your
        decision; it does not change anything in Google itself.
      </p>

      {inbox.status === 'loading' && (
        <div aria-busy="true">
          {[0, 1].map((i) => <div className="skeleton" key={i} style={{ height: '7rem', marginBottom: 12 }} />)}
        </div>
      )}

      {inbox.status === 'empty' && (
        <EmptyBlock title="No requests have come to you">
          When someone requests a folder you own, from Owners or My access, it appears here.
        </EmptyBlock>
      )}

      {open.map((i) => (
        <OpenRequest key={i.requestId} item={i} onDone={(u) => setUpdates((m) => ({ ...m, [u.requestId]: u }))} />
      ))}

      {closed.length > 0 && (
        <>
          <h2 style={{ marginTop: 'var(--s-xl)' }}>Done</h2>
          <div className="tablewrap">
            <table>
              <thead><tr><th>Folder</th><th>Requested by</th><th>Your decision</th><th>When</th></tr></thead>
              <tbody>{closed.map((i) => <ClosedRequest key={i.requestId} item={i} />)}</tbody>
            </table>
          </div>
        </>
      )}
    </>
  )
}
